import { useSnackbar } from "@homebound/beam";
import { useNavigate } from "react-router-dom";
import { WorkflowActionArgs } from "src/routes/projects/change-events/guided/workflows/types";
import { createChangeEventsUrl } from "src/RouteUrls";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";
import { useApplyInitialConfigurationMutation } from "./useApplyConfigurationDirectly.generated";

/**
 * Applies the selected configuration straight onto the project, for a stage with no signed contract.
 *
 * `applyInitialConfig` takes the same input as the change event the wizard previewed with a dry run, and applies it
 * through the same derivation, so the project ends up where the Review Impact step said it would. With no signed
 * contract the change event isn't kept, so there is nothing to submit for approval afterwards.
 */
export function useApplyConfigurationDirectly(args: WorkflowActionArgs<ApplyConfigurationFormInput>) {
  const { form, projectId } = args;
  const navigate = useNavigate();
  const { triggerNotice } = useSnackbar();
  const [applyInitialConfiguration] = useApplyInitialConfigurationMutation();

  return async () => {
    if (!form.canSave()) return;

    await applyInitialConfiguration({ variables: { input: { projectId, targetId: form.targetConfigId.value! } } });

    // Nothing was ever saved as a change event, so drop the form's changes to keep the unsaved-changes guard quiet.
    form.revertChanges();
    triggerNotice({ message: "Configuration applied" });
    navigate(createChangeEventsUrl(projectId));
  };
}
